import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IonPage, IonContent, IonButton, useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import "./Tab2.css";
import styled from "@emotion/styled";
import { Barcode } from "lucide-react";
import { grey } from "@mui/material/colors";
import { Typography } from "@mui/material";
import type { ItemStatus } from "../../types/DB2";
import { useUserControls } from "../../context/UserControlsProvider";
import { CustomThemeType } from "../../context/Providers";
import ShipmentViewAppBar from "../../components/AppBar/ShipmentViewAppBar";
import QRBarcodeScanner from "../../components/QRBarcodeScanner/QRBarcodeScanner";
import { useScanner } from "../../context/ScannerContext";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import { useOfflineSync } from "../../context/OfflineSyncProvider";
import { Haptics, NotificationType } from "@capacitor/haptics";
import { isAndroidWeb, isDesktopWeb, isIOSWeb } from "../../utils/platform";
import { captureCurrentLocation } from "../../utils/location";
import { getPersistedPermission, setPersistedPermission } from "../../utils/cameraPermission";
import { formatStatusLabel } from "../../utils/status";
import { useDeveloperMode } from "../../context/DeveloperModeContext";
import SignatureDevFooter from "../../components/SignatureActions/SignatureDevFooter";
import SignatureCaptureModal from "../../components/SignatureActions/SignatureCaptureModal";
import type { SignatureRole, SignatureCaptureResult } from "../../components/SignatureActions/types";
import { isVerboseLoggingEnabled } from "../../utils/debug";

const StartScanningButton = styled.button<{ theme?: CustomThemeType }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: white;
  color: ${grey[500]};
  &:active {
    background-color: #d1d1d1;
  }
  &:hover {
    background-color: #d1d1d1;
  }
`;

const QuaggaWrapper = styled.div<{ theme?: CustomThemeType }>`
  background-color: ${(p) => p.theme.background};
`;

const StartScanningWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
`;
const Tab2: React.FC = () => {
  const [hasCameraPermissions, setHasCameraPermissions] = useState(false);
  const {
    autoStartScanner,
    setLastScannedItem,
    loadingStatus,
    selectedShipmentIndex,
    updateRecentScannedItemsIds,
    showSnackbar,
    lookupItemInSelectedShipment,
    actionStatus,
    processedGroups,
    unprocessedGroups,
  } = useUserControls();
  const { updateItemStatus } = usePrimaryFetchedData();
  const { isOnline, isForcedOffline } = useOfflineSync();
  const { isScannerActive, startScanner, stopScanner } = useScanner();
  const isProcessingScanRef = useRef(false);
  const processScanRef = useRef<(code: string) => Promise<void>>(async () => {});
  // Toast replaced by global snackbar for consistency
  const scanChimeRef = useRef<HTMLAudioElement | null>(null);
  const requestingRef = useRef(false);
  const wasGrantedEverRef = useRef<boolean>(false);
  const { developerMode, signatureOverrideActive } = useDeveloperMode();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureRole, setCaptureRole] = useState<SignatureRole | null>(null);
  const verbose = useMemo(() => isVerboseLoggingEnabled(), []);

  const processedCount = useMemo(
    () => Object.values(processedGroups).reduce((sum, items) => sum + items.length, 0),
    [processedGroups]
  );
  const unprocessedCount = useMemo(
    () => Object.values(unprocessedGroups).reduce((sum, items) => sum + items.length, 0),
    [unprocessedGroups]
  );
  const itemCount = processedCount + unprocessedCount;
  const isLocationFullyProcessed = itemCount > 0 && processedCount === itemCount;

  const canShowSignatureTools =
    developerMode && (isLocationFullyProcessed || signatureOverrideActive);

  useEffect(() => {
    if (!canShowSignatureTools && captureOpen) {
      setCaptureOpen(false);
      setCaptureRole(null);
    }
  }, [canShowSignatureTools, captureOpen]);

  const logSignatureDebug = useCallback(
    (event: string, meta: Record<string, unknown> = {}) => {
      if (!verbose) {
        return;
      }
      // eslint-disable-next-line no-console
      console.debug("[Tab2 SignatureDebug]", event, {
        developerMode,
        captureOpen,
        captureRole,
        ...meta,
      });
    },
    [captureOpen, captureRole, developerMode, verbose]
  );


  useEffect(() => {
    scanChimeRef.current = new Audio("./assets/scan_chime.wav");
    scanChimeRef.current.preload = "auto";
    scanChimeRef.current.volume = 0.9;
    return () => {
      scanChimeRef.current = null;
    };
  }, []);

  const playErrorTone = useCallback(() => {
    if (typeof window === "undefined" || typeof AudioContext === "undefined") {
      return;
    }
    try {
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "square";
      oscillator.frequency.value = 240;
      gain.gain.value = 0.1;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.22);
      oscillator.onended = () => {
        gain.disconnect();
        context.close().catch((error) =>
          console.warn("[Tab2] audio context close failed", error)
        );
      };
    } catch (error) {
      console.warn("[Tab2] error tone playback failed", error);
    }
  }, []);

  const openSignatureModal = useCallback(
    (role: SignatureRole) => {
      logSignatureDebug("openSignatureModal invoked", { role });
      setCaptureRole(role);
      setCaptureOpen(true);
    },
    [logSignatureDebug]
  );

  const closeSignatureModal = useCallback(() => {
    logSignatureDebug("closeSignatureModal invoked");
    setCaptureOpen(false);
    setCaptureRole(null);
  }, [logSignatureDebug]);

  const handleSignatureSaved = useCallback(
    ({ role }: SignatureCaptureResult) => {
      logSignatureDebug("handleSignatureSaved invoked", { role });
      const friendly = role === "driver" ? "Driver" : "Recipient";
      showSnackbar({
        message: `${friendly} signature captured (dev stub).`,
        severity: "success",
        duration: 4000,
      });
      logSignatureDebug("handleSignatureSaved displayed snackbar", { friendly });
      closeSignatureModal();
    },
    [closeSignatureModal, logSignatureDebug, showSnackbar]
  );


  const requestCameraPermissions = useCallback(async () => {
    if (requestingRef.current) {
      return;
    }
    requestingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      // Immediately stop probing stream; QR component will manage its own stream
      stream.getTracks().forEach((track) => track.stop());
      setHasCameraPermissions(true);
      setPersistedPermission(true);
      startScanner();
    } catch (error: any) {
      console.error("[Tab2] Camera access error:", error);
      setHasCameraPermissions(false);
      const name: string = (error && (error.name || error.code)) || "";
      let message = "Unable to access camera. Please try again.";
      if (/NotAllowedError|PermissionDeniedError/i.test(name)) {
        message = "Camera access blocked. Enable in Settings > Site Permissions.";
      } else if (/NotFoundError|DevicesNotFoundError/i.test(name)) {
        message = "No camera found.";
      } else if (/NotReadableError/i.test(name)) {
        message = "Camera in use by another app.";
      }
      showSnackbar({ message, severity: "error", duration: 6000 });
    } finally {
      requestingRef.current = false;
    }
  }, [showSnackbar, startScanner]);

  useEffect(() => {
    // Read persisted permission once
    wasGrantedEverRef.current = getPersistedPermission();
  }, []);

  useEffect(() => {
    // Platform-aware auto start behavior when arriving on tab
    const onIOS = isIOSWeb();
    if (onIOS) {
      // iOS: require explicit tap every time
      return;
    }
    // Non-iOS (Android/desktop): attempt to start on mount or when autoStart toggled
    if (!hasCameraPermissions) {
      void requestCameraPermissions().catch((err) =>
        console.error("[Tab2] auto-start permission error:", err)
      );
    }
  }, [requestCameraPermissions, hasCameraPermissions]);

  useEffect(() => {
    // Retain existing autoStartScanner behavior; guarded by platform-aware request debouncer
    if (!autoStartScanner || hasCameraPermissions) {
      return;
    }
    void requestCameraPermissions().catch((err) =>
      console.error("[Tab2] auto-start permission error:", err)
    );
  }, [autoStartScanner, hasCameraPermissions, requestCameraPermissions]);

  useEffect(() => {
    // Background/foreground resume handling
    const handleVisibility = () => {
      const onIOS = isIOSWeb();
      if (document.hidden) {
        stopScanner();
        return;
      }
      // visible
      if (onIOS) {
        setHasCameraPermissions(false);
        return;
      }
      if (!hasCameraPermissions && !requestingRef.current) {
        void requestCameraPermissions().catch((err) =>
          console.error("[Tab2] auto-start permission error:", err)
        );
      } else if (hasCameraPermissions && !isScannerActive) {
        startScanner();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [hasCameraPermissions, isScannerActive, requestCameraPermissions, startScanner, stopScanner]);

  // Ensure scanner starts when entering the view if permission already granted
  useIonViewWillEnter(() => {
    if (isIOSWeb()) {
      // iOS requires a gesture; force overlay to re-appear on return
      // so the user must tap to request camera access again
      setHasCameraPermissions(false);
      return;
    }
    if (hasCameraPermissions && !isScannerActive) {
      startScanner();
    } else if (!hasCameraPermissions) {
      void requestCameraPermissions();
    }
  });

  // Optional: stop scanner when leaving the view to free camera immediately
  useIonViewWillLeave(() => {
    if (isScannerActive) {
      stopScanner();
    }
    // Ensure we ask again when coming back on iOS by showing the overlay
    if (isIOSWeb()) {
      setHasCameraPermissions(false);
    }
  });


  useEffect(() => {
    processScanRef.current = async (code: string) => {
      const rawCode = typeof code === "string" ? code.trim() : String(code ?? "");
      const normalizedCode = rawCode.replace(/\s+/g, "");

      if (!normalizedCode) {
        showSnackbar({
          message: "Scanned code was empty.",
          severity: "warning",
          duration: 4000,
        });
        return;
      }

      let item = lookupItemInSelectedShipment(normalizedCode);
      if (!item) {
        item = lookupItemInSelectedShipment(normalizedCode, {
          allowSuffixMatch: true,
        });
      }

      if (!item) {
        showSnackbar({
          message: `Item #${normalizedCode} not found in the selected shipment.`,
          severity: "warning",
          duration: 6000,
        });
        await Haptics.notification({ type: NotificationType.Warning }).catch(
          (error) => console.warn("[Tab2] warning haptic failed", error)
        );
        playErrorTone();
        return;
      }

      const displayCode = (item as any)?.barcodeId ?? normalizedCode;
      const prefixedCode = (() => {
        const p = (item as any)?.prefix;
        const trimmed = typeof p === 'string' ? p.trim() : '';
        return trimmed ? `${trimmed}${displayCode}` : displayCode;
      })();
      const fullItemId = prefixedCode; // use barcode-only (with prefix) for doc id
      const orderId: string | undefined = (item as any)?.orderId;

      const newStatus: ItemStatus = ((): ItemStatus => {
        const fallback: ItemStatus = loadingStatus === "loading" ? "in transit" : "delivered";
        const as = (actionStatus || "").trim();
        const allowed: readonly ItemStatus[] = [
          "pending",
          "in transit",
          "delivered",
          "damaged",
          "missing",
          "delayed",
        ] as const;
        return (allowed as readonly string[]).includes(as) ? (as as ItemStatus) : fallback;
      })();

      const currentStatus = (item as any)?.status as ItemStatus | undefined;
      if (currentStatus && currentStatus === newStatus) {
        showSnackbar({
          message: `Item #${displayCode} already set to "${
            formatStatusLabel(newStatus) || newStatus
          }"`,
          severity: "info",
          duration: 2500,
        });
        return;
      }

      const capturedLocation = await captureCurrentLocation();
      const dropoffPayload = capturedLocation
        ? {
            latitude: capturedLocation.latitude,
            longitude: capturedLocation.longitude,
            accuracyMeters: capturedLocation.accuracyMeters ?? undefined,
            source: capturedLocation.source,
            capturedAt: capturedLocation.timestamp,
          }
        : undefined;
      const success = await updateItemStatus({
        itemId: fullItemId,
        orderId: orderId as string,
        newStatus,
        shipmentIndex: selectedShipmentIndex,
        newProcessedState: true,
        dropoff: dropoffPayload,
        updateMethod: "scan",
        prevStatus: currentStatus,
      });

      if (!success) {
        showSnackbar({
          message: `Item #${displayCode} failed to update.`,
          severity: "error",
          duration: 6000,
        });
        await Haptics.notification({ type: NotificationType.Error }).catch(
          (error) => console.warn("[Tab2] error haptic failed", error)
        );
        playErrorTone();
        return;
      }

      setLastScannedItem(fullItemId);
      const offlineMode = !isOnline || isForcedOffline;
      showSnackbar({
        message: offlineMode
          ? `Item #${displayCode} queued for sync (offline).`
          : `Item #${displayCode} successfully updated.`,
        severity: "success",
        duration: 6000,
      });
      await Haptics.notification({ type: NotificationType.Success }).catch((error) =>
        console.warn("[Tab2] success haptic failed", error)
      );
      await Haptics.vibrate({ duration: 60 }).catch((error) =>
        console.warn("[Tab2] success vibration failed", error)
      );
      updateRecentScannedItemsIds(fullItemId);
      const audio = scanChimeRef.current;
      if (audio) {
        try {
          audio.currentTime = 0;
          void audio.play();
        } catch (error) {
          console.warn("[Tab2] scan chime playback failed", error);
        }
      }
    };
  }, [
    loadingStatus,
    lookupItemInSelectedShipment,
    selectedShipmentIndex,
    setLastScannedItem,
    showSnackbar,
    updateItemStatus,
    updateRecentScannedItemsIds,
    playErrorTone,
    isOnline,
    isForcedOffline,
  ]);

  const handleDetected = useCallback(
    async ({ text, format }: { text: string; format: string }) => {
      if (isProcessingScanRef.current) {
        return;
      }

      // Use top-center snackbar for scan detected message (1.5s)
      showSnackbar({ message: `Scanned (${format}): ${text}`, severity: "info", duration: 1500 });
      isProcessingScanRef.current = true;
      try {
        await processScanRef.current(text);
      } catch (error) {
        console.error("[Tab2] Scan handling error:", error);
      } finally {
        isProcessingScanRef.current = false;
      }
    },
    []
  );

  return (
    <IonPage>
      <ShipmentViewAppBar sourceTag="Tab2-AppBar" />
      <IonContent style={{ paddingTop: 64 }}>
        <QuaggaWrapper className="camera-view-container">
          {!hasCameraPermissions ? (
            <StartScanningWrapper>
              <StartScanningButton
                onClick={() => {
                  // Ensure direct gesture triggers getUserMedia on iOS
                  void requestCameraPermissions();
                }}
              >
                <Barcode
                  size="6rem"
                  style={{ fontSize: "7rem", color: grey[400] }}
                />
                <Typography
                  variant="h5"
                  sx={{ padding: "1rem 0 .25rem 0", color: grey[400] }}
                >
                  {isIOSWeb() && !wasGrantedEverRef.current
                    ? "Tap to Begin"
                    : isIOSWeb() && wasGrantedEverRef.current
                    ? "Tap to Resume Camera"
                    : "Tap to Begin"}
                </Typography>
              </StartScanningButton>
            </StartScanningWrapper>
          ) : (
            <QRBarcodeScanner
              active={isScannerActive}
              onDetected={handleDetected}
            />
          )}
        </QuaggaWrapper>
        {/* Scan-detected feedback now shown via GlobalSnackbar (top-center) */}
      </IonContent>
      {canShowSignatureTools && (
        <>
          <SignatureDevFooter
            onDriverClick={() => openSignatureModal("driver")}
            onRecipientClick={() => openSignatureModal("recipient")}
          />
          <SignatureCaptureModal
            isOpen={captureOpen}
            role={captureRole}
            onDismiss={closeSignatureModal}
            onSave={handleSignatureSaved}
          />
        </>
      )}
    </IonPage>
  );
};

export default Tab2;
